<template>
  <div class="form">
    <div class="form__box">
      <div class="form__block">
        <p>{{ t('fields.firstName') }}</p>
        <ui-input
          :type="'text'"
          :placeholder="t('fields.firstName')"
          :rules="[required]"
          v-model="firstName"
        />
      </div>
      <div class="form__block">
        <p>{{ t('fields.lastName') }}</p>
        <ui-input
          :type="'text'"
          :placeholder="t('fields.lastName')"
          :rules="[required]"
          v-model="lastName"
        />
      </div>
    </div>
    <div class="form__block">
      <p>{{ t('fields.email') }}</p>
      <ui-input
        :type="'text'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
      />
    </div>
    <div class="form__box">
      <div class="form__block">
        <p>{{ t('fields.password') }}</p>
        <ui-input
          :type="'password'"
          :placeholder="t('fields.password')"
          :rules="[isPasswordValid]"
          v-model="password"
        />
      </div>
      <div class="form__block">
        <p>{{ t('fields.passwordConfirm') }}</p>
        <ui-input
          :type="'password'"
          :placeholder="t('fields.passwordConfirm')"
          :rules="[compareStrings]"
          v-model="passwordConfirm"
        />
      </div>
    </div>
    <ui-checkbox
        v-model="isAgree"
    >
      <p>
        {{ t('checkboxes.accept') }}
        <router-link class="form__link" :to="translations.i18nRoute({ name: 'terms-and-conditions' })">
          {{ t('docs.terms.title') }}
        </router-link>
        <span> & </span>
        <router-link class="form__link" :to="translations.i18nRoute({ name: 'privacy-policy' })">
          {{ t('docs.policy.title') }}
        </router-link>
      </p>
    </ui-checkbox>
    <ui-button
        class="form__button"
        :isDisabled="!isFormValid"
        :isLoading="isLoading"
        @click="register"
    >
      {{ t('buttons.createAcc') }}
    </ui-button>
    <p class="form__actions-text">{{ t('register.login') }} <span><router-link class="form__actions-link" :to="translations.i18nRoute({ name: 'login' })">{{ t('buttons.login') }}</router-link></span></p>
  </div>
</template>

<script setup>
import UiInput from "@/components/ui/ui-input.vue";
import {useI18n} from "vue-i18n";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import {computed, h, ref} from "vue";
import UiButton from "@/components/ui/ui-button.vue";
import translations from "@/core/helpers/translations.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";

const {t} = useI18n()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const isAgree = ref(false)

const isLoading = ref(false)
const mailClient = ref(null)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const isFormValid = computed(() => {
  return (
    required(firstName.value) === true &&
    required(lastName.value) === true &&
    isEmail(email.value) === true &&
    isPasswordValid(password.value) === true &&
    compareStrings(passwordConfirm.value) === true &&
    isAgree.value
  )
})

const register = async () => {
  isLoading.value = true

  const res = await Auth.register({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    passwordConfirm: passwordConfirm.value
  })

  if (!res.errors) {
    const domain = email.value.split('@')[1]
    const mailClients = {
      'gmail.com': 'https://mail.google.com/',
      'outlook.com': 'https://outlook.live.com/',
      'icloud.com': 'https://www.icloud.com/',
      'yahoo.com': 'https://mail.yahoo.com/'
    }

    Object.entries(mailClients).forEach((el) => {
      if (domain === el[0]) mailClient.value = el[1]
    })

    ElNotification({
      message: h('div', [
        h('p', t('popup.successRegister')),
        mailClient ? h(
          'button',
          {
            style: {
              marginTop: '10px',
              padding: '6px 12px',
              backgroundColor: '#5B194F',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            },
            onClick: () => {
              window.location.href = mailClient.value
            }
          },
          t('buttons.goEmail')
        ) : ''
      ]),
      type: 'success'
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  position: relative;
  z-index: 1;
  width: 585px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 50px;
  }

  &__block {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;

    & p {
      color: #5B194F;
      font-size: 16px;
      font-weight: 600;
    }
  }

  &__link {
    text-decoration: underline;
    margin-inline: 5px;
    transition: 0.2s;
    color: #5b194f;

    &:hover {
      color: #722363;
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 50px;
  }

  &__actions {
    &-text {
      color: #6C6C6C;
      font-size: 14px;
      font-weight: 400;
    }

    &-link {
      color: #6C6C6C;
      font-size: 14px;
      font-weight: 400;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: auto;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;

      &:hover {
        color: #464646;
      }
    }
  }
}

@media (max-width: 650px) {
  .form {
    width: 100%;
  }
}

@media (max-width: 550px) {
  .form__box {
    flex-direction: column;
    gap: 45px;
  }
}
</style>